import React, { useCallback, useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import '../../assets/stylesheets/gallery-nav.css'
import 'react-toastify/dist/ReactToastify.css'

// Components
import GalleryContainer from './GallerySection/GalleryContainer'
import { InfiniteScrollContainer as InfiniteScroll } from './Assets'
import { LGModal } from '../LG'
import { ModalContainer } from './Modal'

// Hooks
import { useGalleryState } from './hooks/useGalleryState'
import { useNavigation } from './hooks/useNavigation'
import { useSubscriptionManagement } from './hooks/useSubscriptionManagement'
import { useTVRemoteControl } from './hooks/useTVRemoteControl'
import useBackButton from './Controls/BackButton'
import { useUserInfo } from '../Authentication/hooks'
import { useAuthManagement } from '../Header/hooks'

// Utilities
import { useCarousel } from './Carousel'
import { updateCategoryName, deleteCategory, restoreCategory, deleteAsset, restoreAsset, updateAssetName, getUser, updateUser } from '../../assets/requests'
import { toast } from 'react-toastify'

function Gallery() {
  const { userId } = useUserInfo()
  const { isAuthComplete } = useAuthManagement()
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const [showBackModal, setShowBackModal] = useState(false)
  const [categoryLimit, setCategoryLimit] = useState(5)
  const leftArrowRefs = useRef([])
  const rightArrowRefs = useRef([])
  const navigationFetchRef = useRef(false)
  const hasCompletedNavFetch = useRef(false)
  const clickTimeoutRef = useRef(null)
  const categoryCountLock = useRef(false)

  // State management hooks
  const {
    state,
    dispatch,
    combinedNavigation,
    resetState,
    setNavigation,
    setDocuments,
    popSelectedCategory,
    resetSelectedCategory,
    appendSelectedCategory
  } = useGalleryState()

  // Navigation hook
  const { fetchNavigationData, isFetchingRef } =
    useNavigation()

  // Subscription management hook
  const { fetchUserCategoryLimit, applyCategoryLimits } =
    useSubscriptionManagement()

  // Carousel hook
  const { initializeProgressBars, resetSliders } = useCarousel()

  // Back button logic
  const { handleBack } = useBackButton({
    selectedCategories: state.selectedCategories,
    onBackNavigation: popSelectedCategory,
    onShowBackModal: setShowBackModal
  })

  // TV Remote Control hook
  const { resetArrowRefs } = useTVRemoteControl({
    onBrowserBack: handleBack,
    leftArrowRefs,
    rightArrowRefs,
    isModalActive: state.modalActive,
    enableGalleryNavigation: true,
    enableModalNavigation: false,
    enableAssetScrolling: false,
    loadTVStyles: true
  })

  // Suppress double-click events
  useEffect(() => {
    const suppressDoubleClick = event => {
      if (event.detail > 1) {
        event.preventDefault()
      }
    }
    window.addEventListener('mousedown', suppressDoubleClick)
    return () => window.removeEventListener('mousedown', suppressDoubleClick)
  }, [])

  // Initialize state and fetch user limits
  useEffect(() => {
    if (userId) {
      resetState(userId)
      fetchUserCategoryLimit(userId).then(categoryLimit => {
        setCategoryLimit(categoryLimit)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, fetchUserCategoryLimit])

  // Update progress bars when navigation changes
  useEffect(() => {
    initializeProgressBars()
  }, [state.navigation, initializeProgressBars])

  // Fetch navigation data and wait for auth
  useEffect(() => {
    if (userId && isAuthComplete && !navigationFetchRef.current) {
      // Clear stale navigation immediately to prevent flash of empty categories
      setNavigation([])
      // Prevent redirect checks while a fresh navigation fetch is in-flight
      hasCompletedNavFetch.current = false
      const timer = setTimeout(() => {

        navigationFetchRef.current = true
        fetchNavigationData({
          userId,
          selectedCategories: state.selectedCategories,
          assetType: 'image',
          dispatch,
          setNavigation,
          setDocuments
        })
          .catch(() => {})
          .finally(() => {
            navigationFetchRef.current = false
            hasCompletedNavFetch.current = true
          })
      }, 100) // 100ms delay

      return () => clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isAuthComplete, state.selectedCategories.length])

  // Reset sliders when navigation changes
  useEffect(() => {
    if (state.navigation.length > 0) {
      resetSliders()
    }
  }, [state.navigation, resetSliders])

  // Reset arrow refs using the TV remote hook
  useEffect(() => {
    resetArrowRefs()
  }, [resetArrowRefs])

  // Redirect first-time users (no categories, no documents, no assets) to /upload
  useEffect(() => {
    if (!userId || !isAuthComplete) return
    if (state.selectedCategories.length > 0) return
    if (isFetchingRef && isFetchingRef.current) return
    if (!hasCompletedNavFetch.current) return

    const noNavigation = (state.navigation?.length || 0) === 0
    const noDocuments = (state.documents?.length || 0) === 0
    const noAssets = state.showAssets && (state.assets?.length || 0) === 0

    if (noNavigation && noDocuments && noAssets) {
      navigate('/upload', {
        replace: true,
        state: { redirectedFromGallery: true }
      })
    }
  }, [
    userId,
    isAuthComplete,
    state.navigation?.length,
    state.documents?.length,
    state.assets?.length,
    state.showAssets,
    state.selectedCategories.length,
    navigate,
    isFetchingRef
  ])

  const processedNavigation = applyCategoryLimits(combinedNavigation, categoryLimit)
  const processedNavigationForView = processedNavigation

  const handleThumbClick = useCallback(
    elm => {
      if (elm && elm.type === 'year') {
        const documentsSuper = combinedNavigation && combinedNavigation.find(n => n && String(n.name).trim() === 'Documents')
        const isDocumentsYear = documentsSuper && Array.isArray(documentsSuper.subCategories) && documentsSuper.subCategories.some(y => String(y.name).trim() === String(elm.name).trim())
        if (isDocumentsYear) {
          navigate(`/documents/${elm.name}`, { state: { yearNode: elm } })
          return
        }
      }

      if (elm && elm.type === 'document') {
        const url = elm.fileUrl || elm.file_url || elm.s3uri || ''
        if (url) {
          window.open(url, '_blank')
          return
        }
        return
      }

      if (elm.name && elm.name.includes && elm.name.includes('Uncategorized')) {
        navigate('/uncategorized')
        return
      }

      if (elm.subCategories) {
        navigate('/upload')
        return
      }

      appendSelectedCategory(elm)
    }, [navigate, appendSelectedCategory, combinedNavigation]
  )

  const refreshNavigation = useCallback(() => {
    fetchNavigationData({
      userId,
      selectedCategories: state.selectedCategories,
      assetType: 'image',
      dispatch,
      setNavigation,
      setDocuments
    })
  }, [userId, state.selectedCategories, dispatch, setNavigation, setDocuments, fetchNavigationData])

  const handleEditCategoryName = useCallback(
    async (elm, newName) => {
      try {
        await updateCategoryName({ userId, categoryId: elm.id, name: newName })
        refreshNavigation()
      } catch (error) {
        console.error('Error updating category name:', error)
      }
    },
    [userId, refreshNavigation]
  )

  const handleDeleteCategory = useCallback(
    async (elm) => {
      try {
        await deleteCategory({ userId, categoryId: elm.id })

        // Optimistic removal — remove from nested subCategories (kin categories are nested)
        setNavigation(state.navigation.map(group => {
          if (!group.subCategories) return group
          const filtered = group.subCategories.filter(cat => cat.id !== elm.id)
          if (filtered.length === group.subCategories.length) return group
          return { ...group, subCategories: filtered }
        }).filter(group => !group.subCategories || group.subCategories.length > 0))

        // Decrement categoryCount on the user record (fire-and-forget)
        const updateCategoryCount = async (delta) => {
          while (categoryCountLock.current) {
            await new Promise(r => setTimeout(r, 50))
          }
          categoryCountLock.current = true
          try {
            const userData = await getUser(userId)
            const currentCount = userData?.categoryCount || 0
            const newCount = Math.max(0, currentCount + delta)
            if (newCount !== currentCount) {
              await updateUser(userId, { categoryCount: newCount })
            }
          } finally {
            categoryCountLock.current = false
          }
        }

        updateCategoryCount(-1).catch(err =>
          console.error('Failed to update category count:', err)
        )

        const handleUndo = async () => {
          try {
            await restoreCategory({ userId, categoryId: elm.id })
            updateCategoryCount(1).catch(err =>
              console.error('Failed to update category count on restore:', err)
            )
            toast.success('Category restored!', {
              autoClose: 1000,
              hideProgressBar: true
            })
            refreshNavigation()
          } catch (err) {
            console.error('Failed to restore category:', err)
            toast.error('Failed to restore category.', {
              autoClose: 2000,
              hideProgressBar: true
            })
          }
        }

        toast.info(
          ({ closeToast }) => (
            <div>
              Category "{elm.name}" deleted.{' '}
              <button
                onClick={() => { handleUndo(); closeToast(); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#3498db',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: 'inherit',
                  padding: 0
                }}
              >
                Undo
              </button>
            </div>
          ),
          { autoClose: 5000, hideProgressBar: false }
        )
      } catch (error) {
        console.error('Error deleting category:', error)
        toast.error('Failed to delete category.', {
          autoClose: 2000,
          hideProgressBar: true
        })
      }
    },
    [userId, refreshNavigation, setNavigation, state.navigation]
  )

  const handleEditDocumentName = useCallback(
    async (elm, newName) => {
      try {
        await updateAssetName({ userId, assetId: elm.id, name: newName })
        refreshNavigation()
      } catch (error) {
        console.error('Error updating document name:', error)
        toast.error('Failed to rename document.', {
          autoClose: 2000,
          hideProgressBar: true
        })
      }
    },
    [userId, refreshNavigation]
  )

  const handleDeleteDocument = useCallback(
    async (elm) => {
      try {
        await deleteAsset({ userId, assetId: elm.id })
        refreshNavigation()

        const handleUndo = async () => {
          try {
            await restoreAsset({ userId, assetId: elm.id })
            toast.success('Document restored!', {
              autoClose: 1000,
              hideProgressBar: true
            })
            refreshNavigation()
          } catch (err) {
            console.error('Failed to restore document:', err)
            toast.error('Failed to restore document.', {
              autoClose: 2000,
              hideProgressBar: true
            })
          }
        }

        toast.info(
          ({ closeToast }) => (
            <div>
              Document &quot;{elm.name}&quot; deleted.{' '}
              <button
                onClick={() => { handleUndo(); closeToast(); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#3498db',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: 'inherit',
                  padding: 0
                }}
              >
                Undo
              </button>
            </div>
          ),
          { autoClose: 5000, hideProgressBar: false }
        )
      } catch (error) {
        console.error('Error deleting document:', error)
        toast.error('Failed to delete document.', {
          autoClose: 2000,
          hideProgressBar: true
        })
      }
    },
    [userId, refreshNavigation]
  )

  // Clear any pending click timeout on unmount
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current)
        clickTimeoutRef.current = null
      }
    }
  }, [])

  // Wheel handling for smooth scrolling
  const handleWheel = useCallback(event => {
    // This is just passed down as a prop for consistency
  }, [])


  return !userId || !isAuthComplete ? null : (
    <div className="all-assets">
      {showBackModal && (
        <LGModal
          isVisible={showBackModal}
          onClose={() => setShowBackModal(false)}
        />
      )}

      {state.modalActive && (
        <ModalContainer
          state={state}
          dispatch={dispatch}
          onClose={() => dispatch({ type: 'set_modal_active', payload: false })}
          props={{
            state,
            dispatch,
            onClose: () =>
              dispatch({ type: 'set_modal_active', payload: false })
          }}
        />
      )}

      <GalleryContainer
        processedNavigation={processedNavigationForView}
        navigationLength={processedNavigationForView?.length || 0}
        onThumbClick={handleThumbClick}
        onEditName={handleEditCategoryName}
        onDeleteCategory={handleDeleteCategory}
        onEditDocumentName={handleEditDocumentName}
        onDeleteDocument={handleDeleteDocument}
        onHomeClick={resetSelectedCategory}
        onBackClick={popSelectedCategory}
        handleWheel={handleWheel}
        leftArrowRefs={leftArrowRefs}
        rightArrowRefs={rightArrowRefs}
      />

      <div className="infinite-scroll-container">
        <InfiniteScroll
          state={state}
          dispatch={dispatch}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          className="infinite-scroll"
        />
      </div>
    </div>
  )
}

export default Gallery
